import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Note } from '@/store/notesStore';
import { openNoteBody } from '@/services/NoteBodyService';
import { cacheNoteBody, clearNoteBodyCache, getCachedNoteBody } from '@/services/NoteBodyCache';
import { useMasterPasswordStore } from '@/store/masterPasswordStore';
import { useNotesStore } from '@/store/notesStore';
import { triggerErrorMessage } from '@/components/ui/toast';
import { synchronizeNoteWriteSequence } from './useManageNote';

export function useGetNote(uuid: string | undefined) {
  const [note, setNote] = useState<Note | null>(null);
  const [hasKeyWrapper, setHasKeyWrapper] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const promptedRef = useRef(false);

  const completeEncryptionConversion = useCallback((encrypted: boolean, seq: number, body: string) => {
    if (note?.id) {
      synchronizeNoteWriteSequence(note.id, seq);
      clearNoteBodyCache(note.id);
      if (encrypted) {
        cacheNoteBody(note.id, seq, body);
        void loadAllEncryptedHistory(note.id).catch(() => {
          triggerErrorMessage('Could not load all encrypted history. Open a version again to retry.');
        });
      }
    }
    setNote((current) => {
      if (!current) return current;
      const updated = {
        ...current, content: body, encrypted, seq,
        body_locked: false,
      };
      if (updated.id) useNotesStore.getState().updateNote(updated.id, updated);
      return updated;
    });
  }, [note?.id]);

  const loadNote = useCallback(async () => {
    if (!uuid) return;
    try {
      setLoading(true);
      const response = await axios.get(`/api/app/v1/note/${uuid}`);
      const noteData: Note = response.data.note;
      if (noteData.id && noteData.seq !== undefined) synchronizeNoteWriteSequence(noteData.id, noteData.seq);
      setHasKeyWrapper(noteData.has_key_wrapper ?? false);
      if (!noteData.encrypted) {
        setNote({ ...noteData, encrypted: false, body_locked: false });
        setError(null);
        return;
      }

      const seq = noteData.seq ?? 0;
      const cached = noteData.id ? getCachedNoteBody(noteData.id, seq) : undefined;
      if (cached !== undefined) {
        setNote({ ...noteData, content: cached, encrypted: true, body_locked: false });
        setError(null);
        return;
      }

      const master = useMasterPasswordStore.getState();
      if (!master.masterKey) {
        setNote({ ...noteData, content: '', encrypted: true, body_locked: true });
        if (!promptedRef.current) {
          promptedRef.current = true;
          master.openUnlockModal(() => { promptedRef.current = false; void loadNote(); });
        }
        return;
      }

      try {
        const plaintext = await openNoteBody(noteData.id!, noteData.content);
        cacheNoteBody(noteData.id!, seq, plaintext);
        const unlocked = { ...noteData, content: plaintext, encrypted: true, body_locked: false, body_error: undefined };
        setNote(unlocked);
        if (useNotesStore.getState().notes.some((item) => item.id === noteData.id)) {
          useNotesStore.getState().updateNote(noteData.id!, unlocked);
        } else {
          useNotesStore.getState().addNote(unlocked);
        }
        void loadAllEncryptedHistory(noteData.id!).catch(() => {
          triggerErrorMessage('Could not load all encrypted history. Open a version again to retry.');
        });
        setError(null);
      } catch {
        setNote({ ...noteData, content: '', encrypted: true, body_locked: true, body_error: 'Could not decrypt this note. Check your master password and try again.' });
        promptedRef.current = true;
        master.openUnlockModal(() => { promptedRef.current = false; void loadNote(); });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load note');
      setNote(null);
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  const requestUnlock = useCallback(() => {
    promptedRef.current = true;
    useMasterPasswordStore.getState().openUnlockModal(() => {
      promptedRef.current = false;
      void loadNote();
    });
  }, [loadNote]);

  useEffect(() => {
    if (!uuid) {
      setLoading(false);
      return;
    }
    promptedRef.current = false;
    void loadNote();
  }, [loadNote, uuid]);

  return { note, hasKeyWrapper, loading, error, reload: loadNote, requestUnlock, completeEncryptionConversion };
}

async function loadAllEncryptedHistory(noteId: string): Promise<void> {
  const first = await axios.get(`/api/app/v1/note/${noteId}/versions`);
  const revisions: Array<{ id: number; seq: number }> = [...first.data.data];
  let cursor = revisions.length ? Math.min(...revisions.map((row) => row.id)) : 0;
  let hasMore = Boolean(first.data.has_more);
  while (hasMore) {
    const page = await axios.get(`/api/app/v1/note/${noteId}/versions?before_id=${cursor}`);
    const next: Array<{ id: number; seq: number }> = page.data.data;
    revisions.push(...next);
    hasMore = Boolean(page.data.has_more);
    if (next.length) cursor = Math.min(...next.map((row) => row.id));
    else hasMore = false;
  }

  let index = 0;
  const worker = async () => {
    while (index < revisions.length) {
      const revision = revisions[index++];
      const cached = getCachedNoteBody(noteId, revision.seq, revision.id);
      if (cached !== undefined) continue;
      const response = await axios.get(`/api/app/v1/note/${noteId}?content_id=${revision.id}`);
      if (response.data.note.encrypted) {
        const body = await openNoteBody(noteId, response.data.note.content);
        cacheNoteBody(noteId, revision.seq, body, revision.id);
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(3, revisions.length) }, () => worker()));
  useNotesStore.getState().updateNote(noteId, {});
}
